// EventBridge event envelope — base schema every domain event extends.
//
// Every event published to the custom EventBus carries these fields. They
// enable:
//   - `originIdempotencyKey` — propagation per Commitment 8. The producer's
//     idempotency key is passed through every fan-out event so downstream
//     consumers can deterministically dedupe across the whole flow.
//   - `organizationId` — keys per-tenant concurrency on Inngest functions
//     (Commitment 11) and partitions downstream storage.
//   - `occurredAt` — wall-clock at the producing service. Consumers MUST NOT
//     reorder by this field across multiple producers (clock skew); use
//     `eventId` for ordering within a producer.
//   - `eventId` — UUID stamped by the producer. The publisher adapter
//     guarantees uniqueness per event.
//
// Detail-type naming convention: `<entity>.<action>.v<N>` (Commitment 6).
// Bumping the major version (v1 → v2) is a breaking change requiring a
// migration plan; additive evolutions stay on v1.

import { z } from 'zod';

export const eventEnvelopeSchema = z.object({
  // Producers MUST set this to a value that is UNIQUE PER ATTRIBUTABLE EVENT
  // (CharityStack: the per-charge transactionID — each recurring renewal /
  // capture is its own transaction, so it is 1:1 with a donation). Downstream
  // consumers dedupe on it (e.g. the donation bridge passes it as the Inngest
  // event id). If two genuinely-distinct donations ever shared this key, the
  // second would be silently dropped as a duplicate — so the per-event
  // uniqueness guarantee is load-bearing, not advisory.
  originIdempotencyKey: z
    .string()
    .min(1, { message: 'originIdempotencyKey must be non-empty' })
    .max(256),
  organizationId: z
    .string()
    .min(1, { message: 'organizationId must be non-empty' })
    .max(256),
  occurredAt: z.string().datetime({ offset: true }),
  eventId: z.string().uuid({ message: 'eventId must be a UUID' }),
});

export type EventEnvelope = z.infer<typeof eventEnvelopeSchema>;
